/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from 'src/components/Button'
import { CheckBox } from 'src/components/Checkbox'
import { Input } from 'src/components/Input'
import type { StepProps } from './userSelection'

// import dayjs from 'dayjs'
import Icon from 'src/components/Icon'
import { FormDispatcher, State } from '../formController'

import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { useHistory } from 'react-router-dom'
import Banner from 'src/components/Banner'
import BannerController from 'src/components/Banner/BannerController'
import { RequestsService } from '../requests/index'
import { DateOfBirthInput } from 'src/components/ts-components/DateOfBirthInput'
import {
  trackPersonalSignupSubmit,
  trackBusinessSignupStep1,
} from 'src/utils/analytics'
import { readGTMCookies } from '@sportninja/common/utils/cookie'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import {
  heading5,
  paragraphSmall,
  weightBold,
  weightRegular,
} from 'src/components/css'

interface Props extends StepProps, FormDispatcher {
  isBusiness?: boolean
  form?: State
  context: {
    name: string
    id: string
  }
}

export interface ProfileInputFields {
  firstName: string
  lastName: string
  email: string
  dateOfBirthYear: string
  dateOfBirthMonth: string
  dateOfBirthDay: string
  password: string
  confirmPassword: string
}

export const validateFormUsingApi = async (data: any) => {
  try {
    const result = await req('/signup/signup-validate', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return result
  } catch (e) {
    console.error('ERROR', e)
    throw e
  }
}

export type SignUpData = {
  dateOfBirth: string
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  userSignUpType?: string | null
  context: string
  contextId: string
}

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  dateOfBirthYear: '',
  dateOfBirthMonth: '',
  dateOfBirthDay: '',
  password: '',
  confirmPassword: '',
  dateOfBirth: '',
}

const trySignUp = async (
  data: SignUpData,
  gtmData?: any
): Promise<boolean | unknown> => {
  const res = await RequestsService.signUpMember(data, gtmData)

  if (res === 'ERROR') {
    throw new Error('An error happened (ERR: 5)')
  }

  if (res.user && typeof res.user.validated_at === 'string') {
    return true
  }

  return false
}

export const NewMember = ({
  stepDispatcher,
  isBusiness = false,
  formDispatcher,
  context,
  form,
}: Props) => {
  const [loading, setLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] = useState(initialValues)
  const [isPlayer, setIsPlayer] = useState(false)
  const [isTeamOfficial, setIsTeamOfficial] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const history = useHistory()
  const [birthDate, setBirthDate] = useState('')

  useEffect(() => {
    if (window.location.search.includes('shouldRefresh=true')) {
      // create a URL object from the current location
      let url = new URL(window.location.href)
      // get the query parameters
      let params = new URLSearchParams(url.search)
      // remove the shouldRefresh parameter
      params.delete('shouldRefresh')
      // set the new search parameters
      url.search = params.toString()
      // replace the current history entry
      window.history.replaceState({}, document.title, url.toString())
      // refresh the page
      window.location.reload()
    }
  }, [])

  const { control, handleSubmit } = useForm<ProfileInputFields>({
    defaultValues: {
      email: form?.user.email || '',
      firstName: form?.user.firstName || '',
      lastName: form?.user.lastName || '',
      dateOfBirthYear: String(form?.user.dateOfBirth).split('-')[0] || '',
      dateOfBirthMonth: String(form?.user.dateOfBirth).split('-')[1] || '',
      dateOfBirthDay: String(form?.user.dateOfBirth).split('-')[2] || '',
    },
  })

  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  })

  const setCustomErrors = (invalid_fields: any) => {
    if (invalid_fields.name_first) {
      setCustomErroByField('firstName', invalid_fields.name_first)
    }
    if (invalid_fields.name_last) {
      setCustomErroByField('lastName', invalid_fields.name_last)
    }
    if (invalid_fields.email) {
      setCustomErroByField('email', invalid_fields.email)
    }
    if (invalid_fields.birth_date) {
      setCustomErroByField('dateOfBirth', invalid_fields.birth_date)
    }
    if (invalid_fields.password) {
      setCustomErroByField('password', invalid_fields.password)
    }
    if (invalid_fields.confirm_password) {
      setCustomErroByField('confirmPassword', invalid_fields.confirm_password)
    }
  }

  const setCustomErroByField = (field: string, message: string) => {
    setCustomError((prev) => ({ ...prev, [field]: message }))
    setFieldErrors((prevErrors) => [...new Set([...prevErrors, field])])
  }

  const onSubmit = useCallback(
    (data: ProfileInputFields) => {
      setFieldErrors([])
      setCustomError(initialValues)
      setLoading(true)

      const dateOfBirth = birthDate
      const data_to_validate = {
        name_first: data.firstName,
        name_last: data.lastName,
        email: data.email,
        birth_date: dateOfBirth,
        password: data.password,
        confirm_password: data.confirmPassword,
      }
      validateFormUsingApi(data_to_validate)
        .then(() => {
          if (isBusiness) {
            console.log('🔄 NewMember: Business account step 1 completed')
            trackBusinessSignupStep1(data.email, context.name, context.id)
            formDispatcher({
              type: 'setUser',
              payload: {
                dateOfBirth,
                confirmPassword: data.confirmPassword,
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                password: data.password,
              },
            })
            stepDispatcher({ type: 'set', payload: 3 })
            return
          }
          let arr: string[] = []
          if (isPlayer) {
            arr.push('player')
          }
          if (isAdmin) {
            arr.push('admin')
          }
          if (isTeamOfficial) {
            arr.push('team_rep')
          }
          const value = arr.join(',') || null

          // Read GTM tracking data
          const gtmData = readGTMCookies()
          const body: SignUpData = {
            dateOfBirth,
            confirmPassword: data.confirmPassword,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            password: data.password,
            userSignUpType: value,
            context: context.name,
            contextId: context.id,
          }
          console.log('🔄 NewMember: Personal account signup submitted')
          trackPersonalSignupSubmit(
            data.email,
            value || 'unspecified',
            context.name,
            context.id
          )
          trySignUp(body, gtmData)
            .then(() => {
              history.push(ROUTES.SIGNUP_SUCCESS, { email: data.email })
            })
            .catch((e) => {
              console.error('ERROR', e)
              setLoading(false)
            })
        })
        .catch((e) => {
          if (
            e?.status === 500 &&
            e?.message.includes('View [errors.401] not found.')
          ) {
            BannerController.add(({ ...props }) => (
              <Banner
                duration={3000}
                onClose={undefined}
                onClick={undefined}
                subChildren={undefined}
                {...props}
                type='failed'
              >
                {
                  'Looks like you are already logged in. Redirecting you to the home page'
                }
              </Banner>
            ))
            setTimeout(() => {
              window.location.href = ROUTES.HOME
            }, 3000)
            return
          }
          if (e?.invalid_fields) {
            setCustomErrors(e.invalid_fields)
            setLoading(false)
            return
          }
          setLoading(false)
          const errorMessage = getErrorMessage(e)
          BannerController.add(({ ...props }) => (
            <Banner
              duration={undefined}
              onClose={undefined}
              onClick={undefined}
              subChildren={undefined}
              {...props}
              type='failed'
            >
              {errorMessage}
            </Banner>
          ))
        })
    },
    [isPlayer, isAdmin, isTeamOfficial, birthDate]
  )

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit(onSubmit)
      }}
    >
      <div
        css={css`
          display: flex;
          justify-content: center;
        `}
      >
        {isCanlan ? (
          <p
            css={css`
              ${heading5}
              ${weightBold}
                      font-family: ${isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
            `}
          >
            Let’s Create Your Account! 👋
          </p>
        ) : (
          <p
            css={css`
              ${heading5}
              ${weightBold}
                      font-family: ${isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
            `}
          >
            Let’s Create Your {isBusiness ? 'League ' : 'Personal '} Account! 👋
          </p>
        )}
      </div>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          margin: 32px 0;
        `}
      >
        <p
          css={css`
            ${paragraphSmall}
            ${weightRegular}
            color: ${colors.ATTENDANCE_GRAY};
          `}
        >
          *Mandatory field(s)
        </p>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;

          @media (max-width: 768px) {
            flex-direction: column;

            > div {
              width: 100%;
            }
          }
        `}
      >
        <Input
          autoComplete='given-name'
          control={control}
          fieldName='firstName'
          label='First Name *'
          // required
          invalidMessage={customError.firstName}
          invalid={fieldErrors.includes('firstName')}
          tabIndex={1}
        />
        <Input
          autoComplete='family-name'
          control={control}
          fieldName='lastName'
          label='Last Name *'
          // required
          invalidMessage={customError.lastName}
          invalid={fieldErrors.includes('lastName')}
          tabIndex={1}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: 16px;
          @media (max-width: 768px) {
            flex-direction: column;
            > div {
              width: 100%;
            }
          }
        `}
      >
        <Input
          autoComplete='email'
          control={control}
          fieldName='email'
          label='Email *'
          invalidMessage={customError.email}
          invalid={fieldErrors.includes('email')}
          tabIndex={1}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: 16px;
          @media (max-width: 768px) {
            flex-direction: column;
            > div {
              width: 100%;
            }
          }
        `}
      >
        <DateOfBirthInput
          name='dateOfBirth'
          value={birthDate}
          setValue={setBirthDate}
          label='Birth Date'
          fieldErrors={fieldErrors}
          customError={customError}
          tabIndex={1}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;
          margin-top: 16px;

          @media (max-width: 768px) {
            flex-direction: column;

            > div {
              width: 100%;
            }
          }
        `}
      >
        <Input
          autoComplete='new-password'
          control={control}
          fieldName='password'
          label='Password *'
          secureTextEntry={!showPassword.password}
          icon={
            <button
              onClick={() =>
                setShowPassword({
                  ...showPassword,
                  password: !showPassword.password,
                })
              }
            >
              <Icon
                name={!showPassword.password ? 'eye' : 'eye-slash'}
                color={colors.ATTENDANCE_GRAY}
                fontSize={16}
              />
            </button>
          }
          // required
          invalidMessage={customError.password}
          invalid={fieldErrors.includes('password')}
          tabIndex={1}
        />
        <Input
          autoComplete='new-password'
          control={control}
          fieldName='confirmPassword'
          label='Confirm Password *'
          secureTextEntry={!showPassword.confirmPassword}
          icon={
            <button
              onClick={() =>
                setShowPassword({
                  ...showPassword,
                  confirmPassword: !showPassword.confirmPassword,
                })
              }
            >
              <Icon
                name={!showPassword.confirmPassword ? 'eye' : 'eye-slash'}
                color={colors.ATTENDANCE_GRAY}
                fontSize={16}
              />
            </button>
          }
          // required
          invalidMessage={customError.confirmPassword || customError.password}
          invalid={
            fieldErrors.includes('confirmPassword') ||
            fieldErrors.includes('password')
          }
          tabIndex={1}
        />
      </div>
      {!isBusiness && (
        <div
          css={css`
            margin: 16px 0;
            border-bottom: 1px solid ${colors.ATTENDANCE_GRAY};
            border-top: 1px solid ${colors.ATTENDANCE_GRAY};
            padding: 16px 0;
          `}
        >
          <p
            css={css`
              ${paragraphSmall}
              ${weightRegular}
              margin-bottom: 12px;
              color: ${colors.ATTENDANCE_GRAY};
            `}
          >
            You Are (Fill All That Apply):
          </p>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              justify-content: space-between;
              /* if mobile, change the flex direction */
              @media (max-width: 768px) {
                flex-direction: column;
                align-items: flex-start;
                gap: 16px;
              }
            `}
          >
            <CheckBox onChange={(value) => setIsPlayer(value)} label='Player' />
            <CheckBox
              onChange={(value) => setIsTeamOfficial(value)}
              label='Team or Game Official'
            />
            <CheckBox
              onChange={(value) => setIsAdmin(value)}
              label='Administrator'
            />
          </div>
        </div>
      )}
      <div
        css={css`
          padding-top: 16px;
          gap: 16px;
          display: flex;
        `}
      >
        <Button
          onClick={(e) => {
            e.preventDefault()
            if (isCanlan) {
              window.location.href = ROUTES.LOGIN
              return
            }
            stepDispatcher({ type: 'set', payload: 0 })
          }}
          label={'Back'}
          variant='secondary'
          disabled={loading}
        />
        <Button
          tabIndex={1}
          type='submit'
          disabled={loading}
          isLoading={loading}
          label={isBusiness ? 'NEXT' : 'CREATE ACCOUNT'}
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </form>
  )
}
